import { useState } from 'react';
import { GetMyBookingColumnMenu, IColumnMenu, MEMBER_STATUS, NULL_VALUE, orderSort } from "@constants";

import { connect, useDispatch } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Key, SorterResult, TablePaginationConfig } from 'antd/lib/table/interface';

import { Routes } from '@share/constants';
import { GetAllReservationsMobileColumns } from '@constants';
import { getReservationsExport, IReservationsState } from '@share/store/slices';
import { Filters, IFilterField, IReservation, IReservationsFilters, ITextSearch } from '@share/common-types';
import { LoginType, RootState } from '@share/utils';
import { ILoginState } from '@share/store/slices';
import { Action, ReservationAction, SearchTable } from '@components';

import { ReservationsAutocompleteTextSearch } from '../../components/reservations/reservations-text-search';
import { SetFilterField } from '@utils';
import { IInsuranceState } from '@store/slices';

const DEFAULT_MEDIA_POINT = 768;

interface IMapStateToProps {
  loginStore: ILoginState;
  insuranceStore: IInsuranceState;
}

interface IProps extends IMapStateToProps, RouteComponentProps, WrappedComponentProps {
  title: string;

  store: IReservationsState;

  getReservations: (status: string) => void;
  getStatic: (id: number) => void;
  setFilters: (filters: IReservationsFilters) => void;
  setPagination: (pagination: TablePaginationConfig) => void;
  setSort: (sort: SorterResult<IReservation>) => void;
  setBookingType: (bookingType: IFilterField<string>) => void;

  resetFilters: () => void;

  setTextSearch: (textSearch: ITextSearch) => void;
  setTextSearchLabel: (textSearchLabel: string) => void;
}

interface IProps extends IMapStateToProps, RouteComponentProps, WrappedComponentProps { }

function BookingsTableComponent(props: IProps) {

  const [active, setActive] = useState('active');
  const [record, setRecord] = useState<IReservation>();
  const [showMarkAsDetailModal, setShowMarkAsDetailModal] = useState(false);

  const { loginStore, store, insuranceStore, setTextSearch, setTextSearchLabel } = props;
  const { account } = loginStore;
  const { loading, result, pagination, filters } = store;
  const { reservations, totalOrderCount, staticData } = result;
  const dispatch = useDispatch();

  const MyBookingColumnMenu = GetMyBookingColumnMenu();
  if (!account || account.type === LoginType.Public) {
    props.history.push(Routes.NotAuthorized);
  }

  const GetAllReservationsColumns = (): IColumnMenu[] => {
    return MyBookingColumnMenu.map(c => {
      const column = { ...c };
      return column;
    }).sort(orderSort);
  };

  const isFilterApplied = false;
  const isMobile = document.body.offsetWidth <= DEFAULT_MEDIA_POINT;

  const actions: Action[] = [{
    renderAction: () => (
      <>
        <div className="text-search desktop">
          <ReservationsAutocompleteTextSearch
            store={store}
            setTextSearch={setTextSearch}
            setTextSearchLabel={setTextSearchLabel}
            onRefresh={handleTextRefresh}
          />
        </div>
        <div className="button-base-line"></div>
      </>)
  }];

  const columns = isMobile ? GetAllReservationsMobileColumns(active) : GetAllReservationsColumns();
  const dataSource = reservations;

  const filterStatus = (event: any) => {
    event.preventDefault();
    const { store } = props;
    const { pagination } = store;
    const active = event.currentTarget.id;
    setActive(active);
    if (pagination) {
      props.setPagination({ ...pagination, current: 1 });
    }
  }

  const handleOnTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<any>,
  ) => {
    if (sorter) {
      props.setSort({ ...sorter, column: NULL_VALUE });
    }
    if (pagination) {
      props.setPagination(pagination);
    }

    handleApplyFilters(filters);
  }

  const handleApplyFilters = (filters: any) => {
    if (filters) {
      SetFilterField(filters.bookingType, props.setBookingType);
    }
  };

  const handleOnRefresh = () => {
    const { loginStore } = props;
    const { user } = loginStore;
    if (user) {
      props.setSort({
        columnKey: 'createdAt',
        field: 'createdAt',
        order: 'descend',
        column: NULL_VALUE
      });
      SetFilterField(["Hotel~equal"], props.setBookingType);
      props.getReservations(active);
    }
  };

  const handleOnExport = () => {
    dispatch(getReservationsExport(active));
  };

  const handleOnResetFilters = () => {
    props.resetFilters();
  };

  const handleOnFiltersChange = (filters: Filters) => {
    props.setFilters(filters);
  };

  const handleTextRefresh = () => {
    handleOnRefresh();
  }

  const handleOnRowClick = (row: any) => {
    props.getStatic(row.propertyId)
    setRecord(row);
    setShowMarkAsDetailModal(true);
  }

  const closeDetailModal = () => {
    setShowMarkAsDetailModal(false);
  }

  return <>
    <div className="reservations-wrapper">
      
      <ul className="nav nav-pills-shadow nav-responsive">
        <li className="nav-item">
          <a
            className={`nav-link mb-0 me-sm-2 ${active === 'active' && MEMBER_STATUS.ACTIVE}`}
            onClick={filterStatus}
            id="active"
          >
            <FormattedMessage id="active" />
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`nav-link mb-0 me-sm-2 ${active === 'cancelled' && MEMBER_STATUS.ACTIVE}`}
            onClick={filterStatus}
            id="cancelled"
          >
            <FormattedMessage id="cancelled" />
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`nav-link mb-0 me-sm-2 ${active === 'past' && MEMBER_STATUS.ACTIVE}`}
            onClick={filterStatus}
            id="past"
          >
            <FormattedMessage id="past" />
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`nav-link mb-0 me-sm-2 ${active === 'all' && MEMBER_STATUS.ACTIVE}`}
            onClick={filterStatus}
            id="all"
          >
            <FormattedMessage id="all" />
          </a>
        </li>
      </ul>

      <hr style={{ width: '-webkit-fill-available' }} />

      <SearchTable
        key={active}
        title={''}
        loading={loading}
        actions={actions}
        allColumns={[...columns]}
        dataSource={dataSource}
        pagination={pagination}
        filters={filters}
        totalOrderCount={totalOrderCount}
        rowKey={(row: any) => row.key ? `${row.key}` : `${row.bookingId}`}
        isFilterApplied={isFilterApplied}
        onFiltersChange={handleOnFiltersChange}
        onTableChange={handleOnTableChange}
        onRowClick={handleOnRowClick}
        onResetFilters={handleOnResetFilters}
        onApplyFilters={handleApplyFilters}
        onRefresh={handleOnRefresh}
        onExport={handleOnExport}
      ></SearchTable>
    </div>

    {/* <ReservationAction
      loading={loading || insuranceStore?.loading}
      error={insuranceStore?.error}
      reservation={record as IReservation}
      staticData={staticData}
      visible={showMarkAsDetailModal}
      onClose={closeDetailModal}
      onRefresh={handleOnRefresh}
    /> */}
  </>
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    loginStore: state.loginStore,
    insuranceStore: state.insuranceStore,
  };
};

const ComponentIntl = injectIntl(BookingsTableComponent);

const BookingsTable = connect(mapStateToProps)(withRouter(ComponentIntl));
export default BookingsTable;